import _ from 'lodash';

import type { Field, Form, FormResponse, FormResponses, FormResult, Pass } from '@sine/sine-core-api-types';

import type { PrePass } from '../../visit/signin/components/visitorCheckInModal.types';
import passService from '../pass/pass.service';
import type { PrefillFormQuery } from '../pass/pass.types';
import { FieldBooleanSwitch, FieldType, RequiredFalsyValueFieldList } from './constants';
import { booleanSwitchResponseSchema } from './form.schemas';
import type { FieldWithResponses } from './signin.types';
import type { FormWithResponses } from './signin.types';

const eventTypes = {
  signin: 'formResponses',
  signout: 'formResponsesSignOut',
};

const formService = {
  validateFormResponse(pass: Pass | PrePass, fields: Array<FieldWithResponses>) {
    const errors: Array<string> = [];

    fields.forEach((formField) => {
      formField.hasError = false;

      if (!formField.visitorTypes.includes(pass.visitorType)) {
        return;
      }

      if (formField.required) {
        if (
          (!formField.value && !RequiredFalsyValueFieldList.includes(formField.type)) ||
          formField.value?.length === 0
        ) {
          if (formField.type === FieldType.SIGNATURE || formField.type === FieldType.NDA || formField.value === 0) {
            return;
          }

          formField.hasError = true;
          errors.push(`${formField.label} is a required field`);
        }

        if ((formField.allowedValues?.length ?? 0) > 0) {
          const { error } = booleanSwitchResponseSchema(formField.allowedValues).validate(formField);
          if (error) {
            formField.hasError = true;
            errors.push(`${formField.label} ${error.message}`);
          }
        }
      }

      if (formField.type === FieldType.MULTIPLE_CHOICE && formField.allowedValues?.length && formField.value) {
        const idx = formField.options?.indexOf(formField.value);

        if (idx && !formField.allowedOptions?.includes(idx)) {
          formField.hasError = true;
          errors.push(`${formField.label} requires the correct option to be selected`);
        }
      }

      if (formField.value && formField.type === FieldType.NUMBER && typeof formField.value !== 'number') {
        // number field, string and is visible to pass.
        if (typeof formField.value === 'string') {
          // Web - input is type text; strings only.
          const parsedNumber = _.chain(formField.value).replace(',', '.').toNumber().value();
          if (isNaN(parsedNumber)) {
            // not a number
            formField.hasError = true;
            errors.push(`${formField.label} requires a valid number`);
          }
        } else {
          // not a string (Web) or number (iPad)
          formField.hasError = true;
          errors.push(`${formField.label} requires a valid number`);
        }
      }

      if (
        formField.type === FieldType.SELECTIVE_CHOICE &&
        formField.allowedOptions?.length &&
        (formField.required || formField.value !== '')
      ) {
        const requiredSelections: Array<string> = [];

        formField.allowedOptions.forEach((val, index) => {
          const optionName = formField.options?.[val] ?? '';

          if (!formField.value.includes(optionName)) {
            formField.hasError = true;
            formField.mappedOptions![index].hasError = true;
            requiredSelections.push(optionName);
          } else {
            formField.mappedOptions![index].hasError = false;
          }
        });

        if (requiredSelections.length > 0) {
          const requiredString = requiredSelections.join('</li><li>');

          errors.push(
            `${formField.label} requires the following options to be selected:<ul><li>${requiredString}</li></ul>`,
          );
        }
      }
    });

    if (errors.length) {
      return errors;
    }

    return null;
  },

  checkFormVisitorTypes(fields: Array<Field> | undefined, visitorType: string) {
    return fields?.some((field) => field.visitorTypes.includes(visitorType));
  },

  serializeFormResponses(fields: Array<FieldWithResponses> = []) {
    return fields.reduce<Array<FormResponse>>((acc, field) => {
      if (
        field.value ||
        (field.type === FieldType.BOOLEAN_SWITCH && FieldBooleanSwitch.RESPONSE_VALUE_LIST.includes(field.value)) ||
        (field.type === FieldType.NUMBER && field.value === 0)
      ) {
        const contextualValue = field.type === FieldType.NUMBER ? String(field.value) : field.value;

        acc.push({
          id: field.id,
          value: contextualValue,
          type: field.type,
        });
      }
      return acc;
    }, []);
  },

  deserializeFormResponses(fields: Array<FormResponse> = []) {
    const formResponses: Array<FormResponse> = [];

    fields.forEach((field) => {
      if (!_.isUndefined(field.value)) {
        formResponses.push({
          id: field.id,
          value: field.type === FieldType.NUMBER ? Number(field.value) : field.value,
          type: field.type,
        });
      }
    });

    return formResponses;
  },

  async retrieveFormPrefill(siteId: string, pass: Pass | PrePass, event: keyof typeof eventTypes) {
    if (!pass.email && !pass.mobile && !pass.user) {
      return null;
    }

    const { user, email, mobile, visitorType } = pass;
    const params: PrefillFormQuery = {
      user,
      email,
      mobile,
      visitorType,
      fieldName: eventTypes[event],
    };

    return passService
      .prefillForm(siteId, params)
      .then((prefilledForm: FormResponses) => {
        prefilledForm.responses = this.deserializeFormResponses(prefilledForm.responses);
        return prefilledForm;
      })
      .catch(() => {
        return null;
      });
  },

  applyPrefilledFieldByVisitorType(prefilledForm: FormResponses | null, targetForm: Form, passVisitorType: string) {
    if (!prefilledForm?.responses.length) {
      return prefilledForm;
    }

    const visitorTypeQuestions = targetForm.fields.reduce<Array<Field['id']>>((prev, curr) => {
      if (curr.visitorTypes.includes(passVisitorType) && curr.prefillField) {
        prev.push(curr.id);
      }

      return prev;
    }, []);

    prefilledForm.responses = prefilledForm.responses.filter((response) => visitorTypeQuestions.includes(response.id));

    return prefilledForm;
  },

  applyInvitationFormResponseByVisitorType(prefilledForm: FormResult, targetForm: Form, passVisitorType: string) {
    if (!prefilledForm.fields?.length) {
      return prefilledForm;
    }

    const visitorTypeQuestions = targetForm.fields.reduce<Array<Field['id']>>((prev, curr) => {
      if (curr.visitorTypes.includes(passVisitorType)) {
        prev.push(curr.id);
      }

      return prev;
    }, []);

    const { fields, ...restPrefilledForm } = prefilledForm;

    const prefilledFormResponses = fields
      .filter((field) => visitorTypeQuestions.includes(field.id!))
      .map((_field) => {
        const { answer, ...restField } = _field;

        return {
          ...restField,
          value: answer,
        };
      });

    return { responses: prefilledFormResponses, ...restPrefilledForm };
  },

  applyFormToPass(formResponses: FormWithResponses, passProps: Pass) {
    passProps.formResponses = {
      id: formResponses.id,
      responses: this.serializeFormResponses(formResponses.fields),
    };

    return formResponses;
  },
};

export default formService;
